/**
 * Economy: mining, credits, and power (R6, R7, R10).
 *
 * A harvester is a small state machine — seeking, mining, returning,
 * unloading — and the loop runs to steady state without a single explicit
 * order (F1, F2). That property is what makes AE2 checkable at all: if the
 * economy needed micromanagement, a headless match could not measure it.
 *
 * Power degrades production **proportionally** rather than cutting it off:
 * `powerFactor = min(1, produced / consumed)`. That is Red Alert's behaviour,
 * it is a strictly better play experience than a hard stall, and it makes AE4
 * fall out of the state machine instead of needing a special case.
 */

import type { Ctx, Subsystem } from '../core/registry.js'
import * as fix from '../core/fixed.js'
import { MAP_SIZE, MAX_ENTITIES, MAX_PLAYERS } from '../core/config.js'
import { World } from './world.js'
import { Entities, EF, ST, NO_ENTITY } from './entities.js'
import { Pathfind } from './pathfind.js'
import { Vision } from './vision.js'
import { CMD, type Command } from '../net/commands.js'
import { ROLE, defById } from './data/entities.js'

/** Regolith removed from a cell per mining tick. */
const MINE_RATE = 12
/**
 * What a base root pays for ore, against a refinery's full rate. Enough to
 * restart an economy, poor enough that rebuilding the refinery is the obvious
 * move.
 */
const BASE_UNLOAD_RATE = fix.fromFloat(0.45)

/** Ticks a full harvester spends unloading. */
const UNLOAD_TICKS = 45
/** Credits per unit of regolith. */
/** Ticks between a harvester re-evaluating an unreachable target. */
const RETRY_TICKS = 30

export class Economy implements Subsystem {
  static id = 'economy'
  static deps = ['world', 'entities', 'pathfind', 'vision'] as const

  private ctx!: Ctx
  private world!: World
  private entities!: Entities
  private path!: Pathfind
  private vision!: Vision

  /** Per player. */
  readonly credits = new Int32Array(MAX_PLAYERS)
  readonly powerProduced = new Int32Array(MAX_PLAYERS)
  readonly powerConsumed = new Int32Array(MAX_PLAYERS)
  /** Q16.16 in [0, 1]. Multiplies build progress and gates defences. */
  readonly powerFactor = new Int32Array(MAX_PLAYERS).fill(fix.ONE)

  /** Refinery entity id a harvester is bound to, per slot. */
  private readonly boundRefinery = new Int32Array(MAX_ENTITIES)
  /** Cell a harvester is mining, per slot. */
  private readonly mineCell = new Int32Array(MAX_ENTITIES).fill(-1)
  /**
   * How many harvesters have claimed each cell, maintained incrementally.
   *
   * `findField` used to count claims by scanning every entity for every cell —
   * 16,384 cells times 400 entities, per harvester, per seek. The profiler
   * measured 50 ms simulation ticks at four hundred units against an 8 ms
   * budget, and this was almost all of it.
   */
  private readonly claimCount = new Int32Array(MAP_SIZE * MAP_SIZE)
  /** Ticks a harvester has been idle with work available. Feeds AE2. */
  readonly idleTicks = new Int32Array(MAX_ENTITIES)
  /** Peak idle ticks observed per harvester, for the bot-match record. */
  readonly peakIdle = new Int32Array(MAX_ENTITIES)

  /** Refinery id currently occupying each unload bay. One bay per refinery. */
  private readonly bayOccupant = new Map<number, number>()

  init(ctx: Ctx): void {
    this.ctx = ctx
    this.world = ctx.get<World>('world')
    this.entities = ctx.get<Entities>('entities')
    this.path = ctx.get<Pathfind>('pathfind')
    this.vision = ctx.get<Vision>('vision')

    ctx.hash.register('economy:credits', this.credits)
    ctx.hash.register('economy:powerProduced', this.powerProduced)
    ctx.hash.register('economy:powerConsumed', this.powerConsumed)
    ctx.hash.register('economy:powerFactor', this.powerFactor)
    ctx.hash.register('economy:boundRefinery', this.boundRefinery)
    ctx.hash.register('economy:mineCell', this.mineCell)
    ctx.hash.register('economy:claimCount', this.claimCount)
    ctx.hash.register('economy:idleTicks', this.idleTicks)

    ctx.on('actor:removed', (p) => this.onRemoved(p as { id: number; slot: number }))
  }

  /** The only place `mineCell` is written, so the claim count cannot drift. */
  private setMineCell(slot: number, cell: number): void {
    const previous = this.mineCell[slot]
    if (previous === cell) return
    if (previous >= 0 && this.claimCount[previous] > 0) this.claimCount[previous]--
    this.mineCell[slot] = cell
    if (cell >= 0) this.claimCount[cell]++
  }

  private onRemoved(p: { id: number; slot: number }): void {
    this.boundRefinery[p.slot] = 0
    this.setMineCell(p.slot, -1)
    this.idleTicks[p.slot] = 0
    // Free any bay this entity held, whether it was the harvester or the
    // refinery that just died.
    for (const [refinery, harvester] of this.bayOccupant) {
      if (harvester === p.id || refinery === p.id) this.bayOccupant.delete(refinery)
    }
  }

  handleCommand(cmd: Command): boolean {
    if (cmd.t !== CMD.Mine && cmd.t !== CMD.Unload) return false
    for (const id of cmd.units) {
      const slot = this.entities.resolve(id)
      if (slot < 0 || !this.entities.has(slot, EF.Harvester)) continue
      if (cmd.t === CMD.Mine) {
        this.setMineCell(slot, cmd.target)
        this.entities.state[slot] = ST.Seeking
        this.sendTo(slot, cmd.target)
      } else {
        this.boundRefinery[slot] = cmd.target
        this.entities.state[slot] = ST.Returning
      }
    }
    return true
  }

  fixedUpdate(): void {
    this.updatePower()
    this.updateHarvesters()
  }

  // --- power ---------------------------------------------------------------

  private updatePower(): void {
    this.powerProduced.fill(0)
    this.powerConsumed.fill(0)

    const limit = this.entities.limit
    for (let slot = 0; slot < limit; slot++) {
      if (!this.entities.isAlive(slot)) continue
      if (!this.entities.has(slot, EF.Structure)) continue
      // A structure still under construction neither draws nor supplies power.
      if (this.entities.has(slot, EF.Building)) continue
      const owner = this.entities.owner[slot]
      if (owner < 0) continue
      const def = defById(this.entities.kind[slot])
      if (!def) continue
      if (def.power > 0) this.powerProduced[owner] += def.power
      else this.powerConsumed[owner] += -def.power
    }

    for (let p = 0; p < MAX_PLAYERS; p++) {
      const produced = this.powerProduced[p]
      const consumed = this.powerConsumed[p]
      const factor =
        consumed <= 0
          ? fix.ONE
          : produced >= consumed
            ? fix.ONE
            : fix.div(fix.fromInt(produced), fix.fromInt(consumed))
      if (factor !== this.powerFactor[p]) {
        this.powerFactor[p] = factor
        this.ctx.emit('econ:power', { owner: p, produced, consumed, factor })
      }
    }

    // Defences go offline the moment power is short, and come back within one
    // tick when it is restored.
    for (let slot = 0; slot < limit; slot++) {
      if (!this.entities.isAlive(slot)) continue
      if (!this.entities.has(slot, EF.Structure)) continue
      const def = defById(this.entities.kind[slot])
      if (!def || def.role !== ROLE.Defense) continue
      const owner = this.entities.owner[slot]
      const offline = owner >= 0 && this.powerFactor[owner] < fix.ONE
      this.entities.setFlag(slot, EF.Offline, offline)
    }
  }

  /** Q16.16 power factor for a player. */
  factorOf(player: number): fix.Fixed {
    return fix.raw(this.powerFactor[player] ?? fix.ONE)
  }

  // --- credits -------------------------------------------------------------

  creditsOf(player: number): number {
    return this.credits[player] ?? 0
  }

  /** Add credits. Negative amounts are a spend and never take the total below zero. */
  addCredits(player: number, amount: number): void {
    if (player < 0 || player >= MAX_PLAYERS) return
    const next = this.credits[player] + amount
    this.credits[player] = next < 0 ? 0 : next
    this.ctx.emit('econ:credits', { owner: player, credits: this.credits[player], delta: amount })
  }

  /** Spend if affordable. Returns false without deducting otherwise. */
  trySpend(player: number, amount: number): boolean {
    if (amount <= 0) return true
    if (this.credits[player] < amount) return false
    this.addCredits(player, -amount)
    return true
  }

  // --- harvesting ----------------------------------------------------------

  private updateHarvesters(): void {
    const limit = this.entities.limit
    for (let slot = 0; slot < limit; slot++) {
      if (!this.entities.isAlive(slot)) continue
      if (!this.entities.has(slot, EF.Harvester)) continue
      const owner = this.entities.owner[slot]
      if (owner < 0) continue

      const def = defById(this.entities.kind[slot])
      const capacity = def?.capacity ?? 500
      const state = this.entities.state[slot]

      switch (state) {
        case ST.Idle:
        case ST.Scattering:
          // An idle harvester with room seeks work on its own. F1's "the
          // harvester auto-seeks the nearest field" with no explicit order.
          if (this.entities.cargo[slot] >= capacity) this.beginReturn(slot, owner)
          else this.beginSeek(slot, owner)
          break

        case ST.Seeking:
          this.tickSeek(slot, owner)
          break

        case ST.Mining:
          this.tickMining(slot, owner, capacity)
          break

        case ST.Returning:
          this.tickReturning(slot, owner)
          break

        case ST.Unloading:
          this.tickUnloading(slot, owner)
          break

        default:
          break
      }
    }
  }

  private beginSeek(slot: number, owner: number): void {
    const cell = this.findField(slot, owner)
    if (cell < 0 && this.entities.cargo[slot] > 0 && this.hasUnloadPoint(owner)) {
      // Nothing left to mine, but something to deliver.
      this.beginReturn(slot, owner)
      return
    }
    if (cell < 0) {
      // No reachable field at all: idle in a defined state rather than looping
      // path requests every tick. This is **not** counted as idle time — AE2
      // asks about idling while work exists, and a dry map is the end of the
      // economy rather than a stall in it.
      this.entities.state[slot] = ST.Idle
      this.setMineCell(slot, -1)
      this.entities.timer[slot] = RETRY_TICKS
      return
    }
    this.setMineCell(slot, cell)
    this.entities.state[slot] = ST.Seeking
    this.idleTicks[slot] = 0
    this.sendTo(slot, cell)
  }

  private tickSeek(slot: number, owner: number): void {
    const target = this.mineCell[slot]
    if (target < 0 || this.world.regolith[target] <= 0) {
      // The field depleted while inbound. Keep the partial load and find the
      // next nearest field.
      this.beginSeek(slot, owner)
      return
    }
    const here = this.path.unitCell[slot]
    if (here < 0) return
    if (adjacentOrSame(here, target)) {
      this.entities.state[slot] = ST.Mining
      this.entities.timer[slot] = 0
      this.path.stop(slot)
      this.entities.state[slot] = ST.Mining
      return
    }
    // Still travelling. If pathing gave up, ask again after a cool-off.
    if (this.path.goal[slot] < 0) {
      if (--this.entities.timer[slot] <= 0) this.sendTo(slot, target)
    }
    this.idleTicks[slot] = 0
  }

  private tickMining(slot: number, owner: number, capacity: number): void {
    const cell = this.mineCell[slot]
    if (cell < 0 || this.world.regolith[cell] <= 0) {
      // The field ran dry. A harvester with room seeks the next field and
      // **keeps its partial load** rather than hauling a half-empty bucket
      // home; only a full one heads back.
      if (this.entities.cargo[slot] >= capacity) this.beginReturn(slot, owner)
      else this.beginSeek(slot, owner)
      return
    }
    const room = capacity - this.entities.cargo[slot]
    const cx = cell % MAP_SIZE
    const cy = (cell / MAP_SIZE) | 0
    // Priced before the cell is touched: mining it out resets the terrain to
    // plain ground, and a gem load looked up afterwards would be worth what
    // dust is worth.
    const value = this.world.oreValueAt(cx, cy)
    const taken = this.world.mine(cx, cy, Math.min(MINE_RATE, room))
    this.entities.cargo[slot] += taken
    this.entities.cargoValue[slot] += taken * value
    this.idleTicks[slot] = 0

    if (this.entities.cargo[slot] >= capacity) this.beginReturn(slot, owner)
    else if (taken === 0) this.beginSeek(slot, owner)
  }

  private beginReturn(slot: number, owner: number): void {
    const refinery = this.findRefinery(slot, owner)
    if (refinery === NO_ENTITY) {
      // Nowhere to unload at all. Not counted as idle: a player with no
      // refinery has had their economy destroyed, which is losing rather than
      // an economy bug. What AE2 is asking about is a harvester that idles
      // while the machinery to employ it exists.
      this.entities.state[slot] = ST.Idle
      return
    }
    this.boundRefinery[slot] = refinery
    this.entities.state[slot] = ST.Returning
    const rSlot = this.entities.resolve(refinery)
    const cell = this.world.cellAt(fix.raw(this.entities.x[rSlot]), fix.raw(this.entities.y[rSlot]))
    this.sendTo(slot, cell)
    this.idleTicks[slot] = 0
  }

  private tickReturning(slot: number, owner: number): void {
    const refinery = this.boundRefinery[slot]
    const rSlot = this.entities.resolve(refinery)
    if (rSlot < 0) {
      // The refinery died while we were inbound. Reroute, or idle if none left.
      this.beginReturn(slot, owner)
      return
    }
    const here = this.path.unitCell[slot]
    const rCell = this.world.cellAt(fix.raw(this.entities.x[rSlot]), fix.raw(this.entities.y[rSlot]))
    if (here >= 0 && withinCells(here, rCell, 3)) {
      // The bay is self-healing. A harvester that leaves the Unloading state by
      // any other route — scattered off a contested cell, given a move order,
      // killed between ticks — would otherwise hold the bay forever, and every
      // other harvester queues behind it permanently. A sweep seed produced
      // exactly that: six harvesters stuck in Returning with fifty-eight
      // thousand units of explored ore in front of them, for the whole match.
      const holderId = this.bayOccupant.get(refinery)
      if (holderId !== undefined) {
        const holderSlot = this.entities.resolve(holderId)
        if (holderSlot < 0 || this.entities.state[holderSlot] !== ST.Unloading) {
          this.bayOccupant.delete(refinery)
        }
      }

      const holder = this.bayOccupant.get(refinery)
      if (holder !== undefined && holder !== this.entities.idAt(slot)) {
        // The bay is busy. Wait rather than unloading concurrently or, worse,
        // dropping the load. Queueing for a bay **does** count as idle: a
        // refinery that cannot keep up is exactly the economy stall AE2 looks
        // for.
        this.path.stop(slot)
        this.entities.state[slot] = ST.Returning
        this.idleTicks[slot]++
        if (this.idleTicks[slot] > this.peakIdle[slot]) this.peakIdle[slot] = this.idleTicks[slot]
        return
      }
      this.bayOccupant.set(refinery, this.entities.idAt(slot))
      this.entities.state[slot] = ST.Unloading
      this.entities.timer[slot] = UNLOAD_TICKS
      this.path.stop(slot)
      this.entities.state[slot] = ST.Unloading
      return
    }
    if (this.path.goal[slot] < 0) {
      if (--this.entities.timer[slot] <= 0) this.sendTo(slot, rCell)
    }
    this.idleTicks[slot] = 0
  }

  private tickUnloading(slot: number, owner: number): void {
    if (--this.entities.timer[slot] > 0) return

    const amount = this.entities.cargo[slot]
    const worth = this.entities.cargoValue[slot]
    this.entities.cargo[slot] = 0
    this.entities.cargoValue[slot] = 0
    const bound = this.entities.resolve(this.boundRefinery[slot])
    // Ore delivered to a base root instead of a refinery is processed by hand,
    // and paid at a penalty. See `findRefinery`.
    const improvised =
      bound >= 0 && defById(this.entities.kind[bound])?.role === ROLE.BaseRoot
    this.bayOccupant.delete(this.boundRefinery[slot])
    if (amount > 0) {
      const rate = improvised ? BASE_UNLOAD_RATE : fix.ONE
      this.addCredits(owner, fix.toInt(fix.mul(fix.fromInt(worth), rate)))
      this.ctx.emit('harvest:unload', { id: this.entities.idAt(slot), owner, amount, worth })
    }
    this.beginSeek(slot, owner)
  }

  // --- target selection ----------------------------------------------------

  /**
   * Nearest non-depleted regolith cell in **explored** territory, weighted
   * against distance to a refinery so harvesters spread across fields instead
   * of all queuing at the one nearest the base.
   */
  private findField(slot: number, owner: number): number {
    const from = this.path.unitCell[slot]
    if (from < 0) return -1
    const fx = from % MAP_SIZE
    const fy = (from / MAP_SIZE) | 0

    let best = -1
    let bestScore = Infinity
    for (let cell = 0; cell < this.world.cells; cell++) {
      if (this.world.regolith[cell] <= 0) continue
      if (!this.vision.isExplored(owner, cell)) continue
      const cx = cell % MAP_SIZE
      const cy = (cell / MAP_SIZE) | 0
      const d2 = (cx - fx) * (cx - fx) + (cy - fy) * (cy - fy)
      if (d2 >= bestScore) continue
      // Another harvester already working this cell is a soft penalty, not a
      // ban: two harvesters on one rich field beats one idling.
      // A soft penalty per harvester already working this cell, read from the
      // incremental count rather than recounted from every entity.
      const others = this.claimCount[cell] - (this.mineCell[slot] === cell ? 1 : 0)
      const score = d2 + others * 120
      if (score < bestScore && this.path.isReachable(from, cell)) {
        bestScore = score
        best = cell
      }
    }
    return best
  }

  /** Nearest own refinery with a free bay, or the nearest one at all. */
  /**
   * Somewhere to unload: a refinery, or — when the player has none left — the
   * base itself.
   *
   * The fallback exists because losing your last refinery while broke is
   * otherwise unrecoverable, and unrecoverable is not the same as beaten. With
   * no refinery there is no income; with no income there is no refinery; the
   * player then holds a full base and a working harvester fleet and can never
   * act again. A sweep seed sat in exactly that state for twenty-three
   * simulated minutes: 210 credits, three harvesters, no refinery, no income.
   *
   * The base root accepts ore at a penalty (`BASE_UNLOAD_RATE`), so it is a way
   * back rather than a replacement — rebuilding the refinery is still strictly
   * better, and a player who never loses one never notices this exists.
   */
  private findRefinery(slot: number, owner: number): number {
    const from = this.path.unitCell[slot]
    const fallback = !this.hasRefinery(owner)
    let best = NO_ENTITY
    let bestScore = Infinity
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s)) continue
      if (this.entities.owner[s] !== owner) continue
      if (this.entities.has(s, EF.Building)) continue
      const def = defById(this.entities.kind[s])
      const accepts = def?.role === ROLE.Refinery || (fallback && def?.role === ROLE.BaseRoot)
      if (!def || !accepts) continue

      const cell = this.world.cellAt(fix.raw(this.entities.x[s]), fix.raw(this.entities.y[s]))
      const cx = cell % MAP_SIZE
      const cy = (cell / MAP_SIZE) | 0
      const fx = from >= 0 ? from % MAP_SIZE : cx
      const fy = from >= 0 ? (from / MAP_SIZE) | 0 : cy
      let score = (cx - fx) * (cx - fx) + (cy - fy) * (cy - fy)
      const id = this.entities.idAt(s)
      if (this.bayOccupant.has(id)) score += 400
      if (score < bestScore) {
        bestScore = score
        best = id
      }
    }
    return best
  }

  /** A refinery, or a base root standing in for one. */
  private hasUnloadPoint(owner: number): boolean {
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s) || this.entities.owner[s] !== owner) continue
      const role = defById(this.entities.kind[s])?.role
      if (role === ROLE.Refinery || role === ROLE.BaseRoot) return true
    }
    return false
  }

  private hasRefinery(owner: number): boolean {
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s) || this.entities.owner[s] !== owner) continue
      const def = defById(this.entities.kind[s])
      if (def?.role === ROLE.Refinery) return true
    }
    return false
  }

  private sendTo(slot: number, cell: number): void {
    const id = this.entities.idAt(slot)
    if (id === NO_ENTITY) return
    const state = this.entities.state[slot]
    this.path.orderGroup([id], cell, state)
    this.entities.state[slot] = state
    this.entities.timer[slot] = RETRY_TICKS
  }

  /**
   * The longest run of consecutive ticks any harvester spent idle **while a
   * reachable non-depleted field existed** — which is exactly AE2's condition.
   *
   * Counting raw idle ticks would fail the moment the map runs dry, which is
   * not an economy stall but the end of the economy.
   */
  worstIdleTicks(): number {
    let worst = 0
    for (let s = 0; s < this.peakIdle.length; s++) if (this.peakIdle[s] > worst) worst = this.peakIdle[s]
    return worst
  }
}

function adjacentOrSame(a: number, b: number): boolean {
  const ax = a % MAP_SIZE
  const ay = (a / MAP_SIZE) | 0
  const bx = b % MAP_SIZE
  const by = (b / MAP_SIZE) | 0
  return Math.abs(ax - bx) <= 1 && Math.abs(ay - by) <= 1
}

function withinCells(a: number, b: number, r: number): boolean {
  const ax = a % MAP_SIZE
  const ay = (a / MAP_SIZE) | 0
  const bx = b % MAP_SIZE
  const by = (b / MAP_SIZE) | 0
  return Math.abs(ax - bx) <= r && Math.abs(ay - by) <= r
}
